import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuth } from "../../contexts/AuthContext";
import Icon from "../../components/AppIcon";
import rocketLogo from "../../assets/rocket-logo.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard-overview";

  const isDev = import.meta.env.DEV;

  const demoCredentials = [
    {
      id: "global",
      label: "Global Admin",
      description: "Full system access across all tenants",
      email: "gabor_ferencz@octopus-digital.pro",
      password: "Topper14",
      icon: "Shield",
      gradient: "from-[#FF5A5F] to-[#FF8A8E]",
    },
  ];

  const handleDemoLogin = async (demo) => {
    setSelectedDemo(demo.id);
    setEmail(demo.email);
    setPassword(demo.password);
    setError("");
    setIsLoading(true);

    try {
      const result = await login(demo.email, demo.password);

      if (result.success) {
        // Small delay to ensure localStorage is written
        await new Promise((resolve) => setTimeout(resolve, 100));
        navigate(from, { replace: true });
      } else {
        setError(result.error || "Invalid email or password");
      }
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
      setSelectedDemo(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        // Small delay to ensure localStorage is written
        await new Promise((resolve) => setTimeout(resolve, 100));
        navigate(from, { replace: true });
      } else {
        setError(result.error || "Invalid email or password");
      }
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
      setSelectedDemo(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Rocket Tronc</title>
      </Helmet>

      <div className="min-h-screen flex bg-[#f8f9fc]">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a2b4b] via-[#243a5e] to-[#1a2b4b]" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF5A5F] rounded-full opacity-10 blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00C9A7] rounded-full opacity-10 blur-3xl transform -translate-x-1/2 translate-y-1/2" />

          <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
            <div>
              <img
                src={rocketLogo}
                alt="Rocket"
                className="h-12 w-auto brightness-0 invert"
              />
            </div>

            <div className="space-y-8">
              <div>
                <h1 className="text-5xl font-heading font-bold leading-tight mb-4">
                  <span className="text-white">TRONC Management</span>
                  <br />
                  <span className="text-[#FF5A5F]">Made Simple</span>
                </h1>
                <p className="text-lg text-white/80 max-w-md leading-relaxed">
                  Automate your tip distribution, ensure compliance, and keep
                  your team happy with transparent TRONC management.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[#FF5A5F]/20 flex items-center justify-center mb-3">
                    <Icon name="MapPin" size={20} className="text-[#FF5A5F]" />
                  </div>
                  <p className="font-semibold mb-1">Multi-Location</p>
                  <p className="text-sm text-white/60">
                    Manage all your venues from one place
                  </p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[#00C9A7]/20 flex items-center justify-center mb-3">
                    <Icon
                      name="PieChart"
                      size={20}
                      className="text-[#00C9A7]"
                    />
                  </div>
                  <p className="font-semibold mb-1">Fair Distribution</p>
                  <p className="text-sm text-white/60">
                    Customizable allocation rules
                  </p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[#FF5A5F]/20 flex items-center justify-center mb-3">
                    <Icon name="Shield" size={20} className="text-[#FF5A5F]" />
                  </div>
                  <p className="font-semibold mb-1">HMRC Compliant</p>
                  <p className="text-sm text-white/60">
                    Built-in compliance features
                  </p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-[#00C9A7]/20 flex items-center justify-center mb-3">
                    <Icon
                      name="FileText"
                      size={20}
                      className="text-[#00C9A7]"
                    />
                  </div>
                  <p className="font-semibold mb-1">Audit Trail</p>
                  <p className="text-sm text-white/60">Complete transparency</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-white/40">
              &#169; {new Date().getFullYear()} Rocket. All rights reserved.
            </p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <img
                src={rocketLogo}
                alt="Rocket"
                className="h-12 w-auto mx-auto mb-2"
              />
              <p className="text-sm text-muted-foreground">TRONC Management</p>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-3xl font-heading font-bold text-[#1a2b4b]">
                Welcome back
              </h2>
              <p className="text-muted-foreground mt-2">
                Sign in to your account to continue
              </p>
            </div>

            {/* Demo Login Buttons - Only shown in development */}
            {isDev && (
              <div className="bg-gradient-to-br from-[#1a2b4b] to-[#2d4a7c] rounded-2xl p-6 mb-6 shadow-lg">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                    <Icon name="Zap" size={16} className="text-[#FF5A5F]" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">
                      Dev Quick Access
                    </p>
                    <p className="text-xs text-white/60">
                      Click to login instantly
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {demoCredentials.map((demo) => (
                    <button
                      key={demo.id}
                      type="button"
                      onClick={() => handleDemoLogin(demo)}
                      disabled={isLoading}
                      className={`w-full group relative flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                        selectedDemo === demo.id
                          ? "border-[#FF5A5F] bg-[#FF5A5F]/20"
                          : "border-white/10 hover:border-white/30 hover:bg-white/5"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div
                        className={`w-10 h-10 rounded-lg bg-gradient-to-br ${demo.gradient} flex items-center justify-center shrink-0`}
                      >
                        {selectedDemo === demo.id && isLoading ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <Icon
                            name={demo.icon}
                            size={18}
                            className="text-white"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white text-sm">
                          {demo.label}
                        </p>
                        <p className="text-xs text-white/60 truncate">
                          {demo.description}
                        </p>
                      </div>
                      <Icon
                        name="ArrowRight"
                        size={16}
                        className="text-white/40 group-hover:text-white/80 transition-colors shrink-0"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Divider - only show when demo panel is visible */}
            {isDev && (
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#f8f9fc] px-3 text-gray-400 font-medium">
                    Or sign in manually
                  </span>
                </div>
              </div>
            )}

            {/* Login Form */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Error Alert */}
                {error && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600">
                    <Icon name="AlertCircle" size={20} />
                    <span className="text-sm font-medium">{error}</span>
                  </div>
                )}

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-[#1a2b4b] mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                      <Icon name="Mail" size={18} className="text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 text-[#1a2b4b] bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF5A5F]/20 focus:border-[#FF5A5F] transition-all"
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-[#1a2b4b]"
                    >
                      Password
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-[#FF5A5F] hover:text-[#e54e53] hover:underline transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                      <Icon name="Lock" size={18} className="text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-11 pr-12 py-3 text-[#1a2b4b] bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF5A5F]/20 focus:border-[#FF5A5F] transition-all"
                      placeholder="••••••••"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-[#1a2b4b] transition-colors"
                    >
                      <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-[#FF5A5F] hover:bg-[#e54e53] text-white font-semibold rounded-xl shadow-lg shadow-[#FF5A5F]/25 hover:shadow-xl hover:shadow-[#FF5A5F]/30 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
                  disabled={isLoading}
                >
                  {isLoading && !selectedDemo ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <Icon name="ArrowRight" size={18} />
                    </>
                  )}
                </button>
              </form>

              {/* Register link */}
              <p className="text-center text-sm text-gray-500 mt-4">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="text-[#FF5A5F] hover:text-[#e54e53] hover:underline font-medium transition-colors"
                >
                  Start free trial
                </Link>
              </p>
            </div>

            {/* Footer */}
            <p className="text-center text-sm text-gray-400 mt-8 lg:hidden">
              &#169; {new Date().getFullYear()} Rocket. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
